/**
 * Pure layout/data helpers for the schedule Calendar view.
 * Kept separate from the renderer so the math can be unit-tested independently.
 */

import {
  SCHEDULE_CALENDAR_DEFAULT_DURATION_MIN,
  SCHEDULE_CALENDAR_FALLBACK_START_HOUR,
  SCHEDULE_CALENDAR_FALLBACK_END_HOUR,
} from "../constants";
import type { ScheduleItemRow } from "../types/db-types";

/** A schedule day-group restricted to dated entries (never null). */
export interface DatedGroup {
  date: string;
  items: ScheduleItemRow[];
}

/** A timed item placed in the calendar grid, including its lane within its overlap cluster. */
export interface TimedEntry {
  item: ScheduleItemRow;
  startMin: number;
  endMin: number;
  lane: number;
  totalLanes: number;
}

interface SortedEntry {
  item: ScheduleItemRow;
  startMin: number;
  endMin: number;
}

/**
 * Parses an HH:MM[:SS] time string into total minutes from midnight.
 * @param time - "HH:MM" or "HH:MM:SS" or null
 * @returns Total minutes from midnight, or null if input is null/invalid
 */
export function timeToMinutes(time: string | null): number | null {
  if (!time) return null;
  const [hStr, mStr] = time.split(":");
  const h = Number(hStr);
  const m = Number(mStr);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  return h * 60 + m;
}

/** True if the item has a start_time (and therefore belongs on the time grid). */
export function isTimedItem(item: ScheduleItemRow): boolean {
  return item.start_time !== null;
}

/** A schedule day-group keyed by date, where `null` collects undated ("Unscheduled") items. */
export interface DateGroup {
  date: string | null;
  items: ScheduleItemRow[];
}

/**
 * Groups schedule items by their `date`, sorting groups ascending with undated items last.
 * Items within a group preserve their incoming order (callers pre-sort by sort_order/time).
 *
 * @param items - Flat list of schedule items.
 * @returns Date-keyed groups, dated ones ascending followed by the `null` (undated) group if any.
 */
export function groupItemsByDate(items: ScheduleItemRow[]): DateGroup[] {
  const groups = new Map<string | null, ScheduleItemRow[]>();
  for (const item of items) {
    const key = item.date;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(item);
  }
  return Array.from(groups.entries())
    .sort(([a], [b]) => {
      if (a === null) return 1;
      if (b === null) return -1;
      return a.localeCompare(b);
    })
    .map(([date, groupItems]) => ({ date, items: groupItems }));
}

/**
 * Lays out timed items for a single day column: clusters overlapping events
 * and assigns each event a lane index plus the total lane count for its cluster.
 * @param items - Items for one day (mix of timed and untimed; untimed are filtered out)
 * @returns Timed entries with `lane` and `totalLanes` for absolute positioning
 */
export function layoutDayItems(items: ScheduleItemRow[]): TimedEntry[] {
  const sorted: SortedEntry[] = items
    .map<SortedEntry | null>((item) => {
      const startMin = timeToMinutes(item.start_time);
      if (startMin === null) return null;
      const endRaw = timeToMinutes(item.end_time);
      const endMin = endRaw !== null && endRaw > startMin ? endRaw : startMin + SCHEDULE_CALENDAR_DEFAULT_DURATION_MIN;
      return { item, startMin, endMin };
    })
    .filter((x): x is SortedEntry => x !== null)
    .sort((a, b) => a.startMin - b.startMin || a.endMin - b.endMin);

  const result: TimedEntry[] = [];
  let cluster: SortedEntry[] = [];
  let clusterEnd = -Infinity;

  const flushCluster = (): void => {
    if (cluster.length === 0) return;
    const laneEnds: number[] = [];
    const assignments: { entry: SortedEntry; lane: number }[] = [];
    for (const entry of cluster) {
      let lane = laneEnds.findIndex((end) => end <= entry.startMin);
      if (lane === -1) {
        lane = laneEnds.length;
        laneEnds.push(entry.endMin);
      } else {
        laneEnds[lane] = entry.endMin;
      }
      assignments.push({ entry, lane });
    }
    const totalLanes = laneEnds.length;
    for (const { entry, lane } of assignments) {
      result.push({ item: entry.item, startMin: entry.startMin, endMin: entry.endMin, lane, totalLanes });
    }
  };

  for (const entry of sorted) {
    if (entry.startMin >= clusterEnd) {
      flushCluster();
      cluster = [entry];
      clusterEnd = entry.endMin;
    } else {
      cluster.push(entry);
      clusterEnd = Math.max(clusterEnd, entry.endMin);
    }
  }
  flushCluster();
  return result;
}

/**
 * Computes the displayed hour range that covers every timed item across the given groups.
 * Falls back to SCHEDULE_CALENDAR_FALLBACK_* when no timed items exist.
 * @param groups - Dated groups to inspect
 * @returns Inclusive `[startHour, endHour]` (hours of day, 0-24)
 */
export function computeHourRange(groups: DatedGroup[]): { startHour: number; endHour: number } {
  let minMin: number | null = null;
  let maxMin: number | null = null;
  for (const group of groups) {
    for (const item of group.items) {
      const sm = timeToMinutes(item.start_time);
      if (sm === null) continue;
      const emRaw = timeToMinutes(item.end_time);
      const em = emRaw !== null && emRaw > sm ? emRaw : sm + SCHEDULE_CALENDAR_DEFAULT_DURATION_MIN;
      minMin = minMin === null ? sm : Math.min(minMin, sm);
      maxMin = maxMin === null ? em : Math.max(maxMin, em);
    }
  }
  if (minMin === null || maxMin === null) {
    return {
      startHour: SCHEDULE_CALENDAR_FALLBACK_START_HOUR,
      endHour: SCHEDULE_CALENDAR_FALLBACK_END_HOUR,
    };
  }
  const sH = Math.max(0, Math.floor(minMin / 60));
  const eH = Math.min(24, Math.ceil(maxMin / 60));
  return { startHour: sH, endHour: Math.max(eH, sH + 1) };
}

/** Returns the inclusive hour list `[startHour, startHour+1, ..., endHour]`. */
export function hoursInRange(startHour: number, endHour: number): number[] {
  const out: number[] = [];
  for (let h = startHour; h <= endHour; h++) out.push(h);
  return out;
}
